/**
 * @overview a component that catches and logs errors thrown in its children.
 */

import React from 'react'
import PropTypes from 'prop-types'
import { track as trackError } from 'lib/utils/errors'

class ErrorBoundary extends React.Component {
  static propTypes = {
    /**
     * Fallback component to display when uncaught exceptions occur in a component tree:
     * ```
     * function({ error: PropTypes.object, errorInfo: PropTypes.object }): PropTypes.node
     * ```
     */
    errorComponent: PropTypes.func,
    /**
     * Callback prop for handling any additional logging or async behavior. It does not impact component rendering:
     * ```
     * function(error, errorInfo) {}
     * ```
     */
    onError: PropTypes.func
  }

  static defaultProps = {
    errorComponent: () => null
  }

  state = {
    error: null,
    errorInfo: null
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    })

    trackError(error, errorInfo)

    if (typeof this.props.onError === 'function') {
      // Wrapped in a promise so we can do this in a non-blocking way
      Promise.resolve()
        .then(() => this.props.onError(error, errorInfo))
        .catch(trackError)
    }
  }

  render() {
    const ErrorComponent = this.props.errorComponent

    return this.state.error ? (
      <ErrorComponent
        error={this.state.error}
        errorInfo={this.state.errorInfo}
      />
    ) : (
      this.props.children
    )
  }
}

export default ErrorBoundary
